import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/common/Button';
import { AddQuestionForm } from '../components/instructor/AddQuestionForm';
import { QuestionList } from '../components/instructor/QuestionList';
import { CreateTestSetForm } from '../components/instructor/CreateTestSetForm';
import { TestSetList } from '../components/instructor/TestSetList';
import { questionService, CreateQuestionData } from '../services/questionService';
import { testSetService } from '../services/testSetService';
import { authService } from '../services/authService';
import toast from 'react-hot-toast';
import { FullPageSpinner } from '../components/common/Spinner';
import { Question, TestSet, User, TestAttempt } from '../types/models';
import { StudentStats } from '../components/instructor/StudentStats';
import { AddStudentForm } from '../components/instructor/AddStudentForm';
import { userService } from '../services/userService';
import { attemptService } from '../services/attemptService';

const pageStyle: React.CSSProperties = {
  maxWidth: '800px',
  margin: '0 auto',
  padding: '2rem',
};

export const InstructorDashboard = () => {
  const { user } = useAuth();
  const [questions, setQuestions] = useState<Question[]>([]);
  const [testSets, setTestSets] = useState<TestSet[]>([]);
  const [students, setStudents] = useState<User[]>([]);
  const [attempts, setAttempts] = useState<TestAttempt[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const fetchData = useCallback(async () => {
    try {
      setIsLoading(true);
      const [
        fetchedQuestions, 
        fetchedTestSets, 
        fetchedStudents, 
        fetchedAttempts
      ] = await Promise.all([
        questionService.getAllQuestions(),
        testSetService.getAllTestSets(),
        userService.getAllStudents(),
        attemptService.getAllAttempts(),
      ]);
      setQuestions(fetchedQuestions);
      setTestSets(fetchedTestSets.sort((a,b) => a.name.localeCompare(b.name)));
      setStudents(fetchedStudents);
      setAttempts(fetchedAttempts);
    } catch (error) {
      console.error("Błąd podczas pobierania danych", error);
      toast.error("Nie udało się pobrać danych.");
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleAddQuestion = async (questionData: CreateQuestionData) => {
    const promise = questionService.addQuestion(questionData);
    toast.promise(promise, {
      loading: 'Dodawanie pytania...',
      success: (newQuestion) => {
        setQuestions(prevQuestions => [...prevQuestions, newQuestion]);
        return 'Pytanie dodane pomyślnie!';
      },
      error: 'Nie udało się dodać pytania.',
    });
    await promise;
  };

  const handleSetCreated = (newSet: TestSet) => {
    setTestSets(prevSets => [...prevSets, newSet].sort((a,b) => a.name.localeCompare(b.name)));
    toast.success('Zestaw został utworzony jako szkic.');
  };

  const handleTogglePublication = async (setId: string, newStatus: boolean) => {
    const promise = testSetService.toggleTestSetPublication(setId, newStatus);
    toast.promise(promise, {
      loading: 'Aktualizowanie statusu...',
      success: (updatedSet) => {
        setTestSets(prevSets => 
          prevSets.map(set => set.id === setId ? updatedSet : set)
        );
        return `Zestaw "${updatedSet.name}" został ${newStatus ? 'opublikowany' : 'ukryty'}.`;
      },
      error: 'Wystąpił błąd podczas aktualizacji.',
    });
  };

  const handleLogout = async () => {
    await authService.logout();
    toast.success('Wylogowano pomyślnie.');
  };

  if (isLoading) {
    return <FullPageSpinner />;
  }

  return (
    <div style={pageStyle}>
      <header style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <h1>Panel Instruktora</h1>
          <h2>Witaj, {user?.firstName}!</h2>
        </div>
        <Button onClick={handleLogout} variant="secondary">Wyloguj</Button>
      </header>
      <main>
        <section>
          <h2>Statystyki Kursantów</h2>
          <StudentStats students={students} attempts={attempts} />
          <AddStudentForm onStudentAdded={fetchData} />
        </section>

        <section>
          <h2>Zarządzaj Pytaniami</h2>
          <AddQuestionForm onAddQuestion={handleAddQuestion} />
          <QuestionList 
            questions={questions} 
            onQuestionUpdated={(updated) => setQuestions(prev => prev.map(q => q.id === updated.id ? updated : q))}
          />
        </section>

        <section>
          <h2>Zarządzaj Zestawami Testowymi</h2>
          <CreateTestSetForm 
            availableQuestions={questions} 
            onSetCreated={handleSetCreated} 
          />
          <TestSetList 
            testSets={testSets}
            onTogglePublication={handleTogglePublication}
            allQuestions={questions}
            onTestSetUpdated={(updated) => setTestSets(prev => prev.map(s => s.id === updated.id ? updated : s))}
            onTestSetDeleted={(id) => setTestSets(prev => prev.filter(s => s.id !== id))}
          />
        </section>
      </main>
    </div>
  );
};